import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
  CatalogEntry,
  Connector,
  ConnectorDiscovery,
  ConnectorsService,
} from '../../../services/connectors.service';
import {
  NotificationToasterService,
  NotificationType,
  V2NotificationDataBuilder,
} from '../../../services/notification-toaster.service';
import { ToasterPopupStyle } from '../../../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';

/**
 * Institution connectors — the services the AI teammate can act against.
 *
 * Only institution admins ever see this: it is a tab of Settings → AI coworker,
 * which renders for them alone, and that is what enforces "non-admins cannot add
 * MCP servers" without a separate permission check.
 *
 * The flow is deliberately two-step — **discover, then connect** — because the two
 * questions have completely different remedies and the admin should know which one
 * they are facing before committing:
 *
 *   * a provider that supports dynamic client registration is one click; we register
 *     ourselves with them at runtime and no human ever visits their developer portal
 *   * a provider that does not needs the admin to create an OAuth app first and paste
 *     a client id/secret
 *
 * Discovery is a pure read that stores nothing, so it is safe to run against whatever
 * URL was typed.
 */
@UntilDestroy()
@Component({
  selector: 'app-user-settings-connectors',
  templateUrl: './user-settings-connectors.component.html',
  styleUrls: ['./user-settings-connectors.component.scss'],
  // OnPush per project policy. Every async callback below therefore has to call
  // markForCheck() — with OnPush, assigning a field inside a subscribe() does not
  // schedule a re-render on its own, and the view silently keeps the old value.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSettingsConnectorsComponent implements OnInit {
  @Input() isMobile = false;
  @Input() PANELS: any;
  // Shown as a tab of the AI coworker panel, which supplies the header.
  @Input() embedded = false;
  @Output() goBack = new EventEmitter<any>();

  connectors: Connector[] = [];
  isLoading = false;

  /**
   * Icon URL per connector key, taken from the catalog.
   *
   * The URL lives on the catalog ROW, not in the frontend bundle: adding a
   * service has to stay an insert. Bundling the images would have meant a
   * frontend build and deploy to introduce one connector — reintroducing exactly
   * the coupling this catalog was moved into Mongo to remove.
   *
   * Also keeps the connected-connector cards in step: they carry a key, and the
   * icon for that key is whatever the catalog says today.
   */
  private iconByKey = new Map<string, string>();

  /**
   * Icon by MCP HOST, for a connector that has no catalog row at all.
   *
   * Anything added through "add by URL" is keyed however the admin named it, so
   * it will never match a catalog key — the institution's own `jiraconfluence`
   * points at mcp.atlassian.com, which IS catalogued (as `atlassian`) and does
   * have an icon. Matching on host recovers it, and does the same for any future
   * hand-added connector pointing at a service we already know.
   */
  private iconByHost = new Map<string, string>();

  /** Add-connector form state. */
  showAdd = false;
  mcpUrl = '';
  label = '';
  key = '';
  clientId = '';
  apiKey = '';
  clientSecret = '';
  discovery?: ConnectorDiscovery;
  discoverError = '';
  isDiscovering = false;
  isCreating = false;
  /** Set when the provider will not register us automatically, so the form asks for
   *  a manually-created OAuth client instead. */
  needsManualClient = false;
  /** Set once the admin edits the key by hand, so we stop overwriting it. */
  keyTouched = false;

  /** The vetted services. Picking one supplies name, identifier and URL, so the
   *  admin never has to know any of them — pasting a URL stays available for
   *  anything we have not catalogued, including a customer's own MCP server. */
  catalog: CatalogEntry[] = [];
  /** true = the paste-a-URL fallback instead of the catalog. */
  manualMode = false;
  picked?: CatalogEntry;
  /** Catalog filter. A scrollable list works at twenty services and stops working
   *  well before a hundred, which is the direction this is going. */
  search = '';

  constructor(
    private connectorsService: ConnectorsService,
    private notificationToaster: NotificationToasterService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.reload();
    this.connectorsService
      .catalog()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.catalog = res.catalog || [];
          this.iconByKey = new Map(
            this.catalog
              .filter((e) => !!e.iconUrl)
              .map((e) => [e.key, e.iconUrl as string]),
          );
          this.iconByHost = new Map(
            this.catalog
              .filter((e) => !!e.iconUrl && !!e.mcpUrl)
              .map((e) => [this.hostOf(e.mcpUrl as string), e.iconUrl as string])
              .filter(([h]) => !!h) as [string, string][],
          );
          this.cdr.markForCheck();
        },
        // A missing catalog must not block the fallback: the admin can still
        // paste a URL, which is the path that guarantees coverage anyway.
        error: () => {
          this.catalog = [];
          this.cdr.markForCheck();
        },
      });
  }

  reload(): void {
    this.isLoading = true;
    this.connectorsService.list().pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        this.connectors = res.connectors || [];
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.toastError('Could not load connectors');
        this.cdr.markForCheck();
      },
    });
  }

  openAdd(): void {
    this.showAdd = true;
    this.manualMode = false;
    this.search = '';
    this.resetAddForm();
  }

  /**
   * Catalog entries matching the search, already-connected ones last.
   *
   * Matches label AND blurb, because an admin looking for "tickets" or "logs" is
   * searching by what they want to DO, not by a brand they may not know we support.
   * Already-connected services stay in the list rather than being filtered out —
   * seeing "Notion — Added" answers "did I already do this?", which vanishing
   * would not.
   */
  get filteredCatalog(): CatalogEntry[] {
    const q = this.search.trim().toLowerCase();
    const matches = q
      ? this.catalog.filter(
          (e) =>
            e.label.toLowerCase().includes(q) ||
            (e.blurb || '').toLowerCase().includes(q) ||
            e.key.toLowerCase().includes(q),
        )
      : this.catalog;
    return [...matches].sort(
      (a, b) => Number(!!a.alreadyConnected) - Number(!!b.alreadyConnected),
    );
  }

  /** A search that finds nothing is the moment to offer the fallback, not a
   *  dead end — the whole point of add-by-URL is that our catalog is not the
   *  limit of what can be connected. */
  get noSearchResults(): boolean {
    return !!this.search.trim() && this.filteredCatalog.length === 0;
  }

  /**
   * Can this entry be connected right now, by this admin, on their own?
   *
   * `needs_allowlist` cannot: the provider only accepts sign-in from addresses it
   * has approved in advance, so registration is refused before any consent screen
   * appears. Nothing the admin does changes that — Pencil has to be added to the
   * provider's list once, for every institution at once. Leaving these clickable
   * meant filling in the form and then being bounced with the provider's raw
   * error, which reads like a Pencil bug rather than a pending business step.
   *
   * Engineering clears one by flipping `setupPath` to `one_click` in the
   * `connectorCatalog` collection once the provider has allowlisted us — a data
   * change, no deploy.
   */
  isBlocked(entry: CatalogEntry): boolean {
    return (
      // §6 — a multi-instance service is never "done". An org connects support@,
      // then contactus@, then security@, so being connected once must not grey the
      // tile out; that was the single thing preventing a second mailbox.
      (!!entry.alreadyConnected && !entry.multiInstance) ||
      entry.setupPath === 'gated' ||
      entry.setupPath === 'needs_allowlist'
    );
  }

  /** Label for a catalog tile: "Added" is wrong for something you can add again. */
  catalogActionLabel(entry: CatalogEntry): string {
    if (entry.alreadyConnected && entry.multiInstance) {
      return 'Add another account';
    }
    return entry.alreadyConnected ? 'Added' : '';
  }

  /** Choose a vetted service. Everything the form needs comes from the entry. */
  pick(entry: CatalogEntry): void {
    if (this.isBlocked(entry)) {
      return;
    }
    this.picked = entry;
    this.mcpUrl = entry.mcpUrl || '';
    this.label = entry.label;
    this.key = entry.key;
    // PIN it. The catalog's key is curated and is what every per-key lookup keys
    // on — `alreadyConnected`, the icon, the blurb, the setup note. Without this,
    // the discovery step below re-derives the key from the LABEL whenever
    // keyTouched is false, so picking "Jira & Confluence" from the catalog stored
    // `jiraconfluence` instead of `atlassian`: the picker then went on offering
    // the service as available and an admin could connect it a second time, with
    // a second credential. Any catalog service whose key differs from its label
    // was affected, not just this one.
    this.keyTouched = true;
    this.needsManualClient = entry.setupPath === 'manual_client';
    this.discoverError = '';
    this.discovery = undefined;
  }

  /** Fall back to pasting a URL — for a service we have not catalogued, or a
   *  customer's own internal MCP server. */
  useManual(): void {
    this.manualMode = true;
    this.picked = undefined;
    this.resetAddForm();
  }

  backToCatalog(): void {
    this.manualMode = false;
    this.picked = undefined;
    this.resetAddForm();
  }

  /** Path to this service's icon, or null when we don't have one. */
  iconFor(key: string, mcpUrl?: string): string | null {
    const byKey = this.iconByKey.get(key);
    if (byKey) {
      return byKey;
    }
    // No catalog row (added by URL) — fall back to whatever we have for the same
    // host, so a hand-added Atlassian connector still shows Atlassian's mark.
    const host = mcpUrl ? this.hostOf(mcpUrl) : '';
    return (host && this.iconByHost.get(host)) || null;
  }

  private hostOf(url: string): string {
    try {
      return new URL(url).host.toLowerCase().replace(/^www\./, '');
    } catch {
      return '';
    }
  }

  /** Fallback mark: the service's own initial, on a colour derived from its key
   *  so the same service always looks the same without storing anything. */
  initialFor(label: string): string {
    return (label || '?').trim().charAt(0).toUpperCase();
  }

  hueFor(key: string): number {
    let h = 0;
    for (let i = 0; i < key.length; i += 1) {
      h = (h * 31 + key.charCodeAt(i)) % 360;
    }
    return h;
  }

  setupLabel(e: CatalogEntry): string {
    // An open server needs no credential at all. Without this it falls through to
    // "Not available yet" — which is the opposite of true, and the least likely
    // badge to make anyone click.
    if (e.setupPath === 'no_auth') {
      return this.translateService.instant('No sign-in needed');
    }
    if (e.setupPath === 'api_key') {
      return this.translateService.instant('Bring your own API key');
    }
    if (e.setupPath === 'one_click') {
      return this.translateService.instant('One click');
    }
    // Deliberately reads as blocked, not as a task: the admin cannot clear this
    // one — Pencil has to be allowlisted by the provider first. The row is
    // disabled to match, and the setupNote says who to ask.
    if (e.setupPath === 'needs_allowlist') {
      return this.translateService.instant('Awaiting provider approval');
    }
    if (e.setupPath === 'manual_client') {
      return this.translateService.instant('Needs your own OAuth app');
    }
    return this.translateService.instant('Not available yet');
  }

  cancelAdd(): void {
    this.showAdd = false;
    this.resetAddForm();
  }

  private resetAddForm(): void {
    this.mcpUrl = '';
    this.label = '';
    this.key = '';
    this.clientId = '';
    this.clientSecret = '';
    this.discovery = undefined;
    this.discoverError = '';
    this.needsManualClient = false;
    this.keyTouched = false;
    this.picked = undefined;
  }

  /** Probe the URL so the admin sees the scopes and whether this is one-click. */
  discover(): void {
    const url = (this.mcpUrl || '').trim();
    if (!/^https:\/\//i.test(url)) {
      this.discoverError = this.translateService.instant(
        'Enter the https URL of the service’s MCP server',
      );
      return;
    }
    this.isDiscovering = true;
    this.discoverError = '';
    this.discovery = undefined;
    this.connectorsService
      .discover(url)
      .pipe(untilDestroyed(this))
      .subscribe({
      next: (res) => {
        this.isDiscovering = false;
        this.discovery = res.discovery;
        if (res.discovery?.problem) {
          this.discoverError = res.discovery.problem;
          return;
        }
        this.needsManualClient = res.discovery?.dcr === 'none';
        if (!this.label) {
          this.label = this.hostLabel(url);
        }
        if (!this.keyTouched) {
          this.key = this.deriveKey(this.label);
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.isDiscovering = false;
        this.discoverError = this.translateService.instant(
          'Could not reach that server',
        );
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Guess a human name for the service from its URL.
   *
   * Vendors spell their MCP hostname at least two ways — `mcp.notion.com` and
   * `netlify-mcp.netlify.app` — so stripping a leading `mcp.` alone produced
   * "Netlify-mcp" and made an admin distrust the field. Handle both shapes, and
   * fall back to the registrable domain rather than emitting something odd.
   */
  private hostLabel(url: string): string {
    try {
      const host = new URL(url).hostname.toLowerCase();
      const parts = host.split('.').filter(Boolean);
      // Drop an `mcp` label wherever it sits, and any `-mcp` / `mcp-` affix.
      const cleaned = parts
        .filter((p) => p !== 'mcp' && p !== 'www')
        .map((p) => p.replace(/-mcp$|^mcp-/, ''));
      // Prefer the registrable name (second-from-last) when the first label is
      // just a subdomain like "api"; otherwise the first meaningful label.
      const candidate =
        cleaned.length > 2 ? cleaned[cleaned.length - 2] : cleaned[0] || host;
      return candidate.charAt(0).toUpperCase() + candidate.slice(1);
    } catch {
      return '';
    }
  }

  /** Identifier the agent uses. Derived from the name so an admin never has to
   *  invent one; still editable for the rare collision. */
  private deriveKey(label: string): string {
    return label.toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /** Keep the key in step while the admin edits the name, unless they have
   *  deliberately overridden it. */
  onLabelChange(): void {
    if (!this.keyTouched) {
      this.key = this.deriveKey(this.label);
    }
  }

  /** A provider we already support directly (Google Workspace). There is no MCP
   *  server to probe, so the whole discover step is skipped — leaving it in would
   *  gate the Connect button behind a check that can never pass. */
  get isDirect(): boolean {
    return this.picked?.kind === 'direct';
  }

  /** Paste-a-key service. No consent flow and no server to probe — the key IS the
   *  connection, so Add is enabled as soon as one is entered. */
  get isApiKey(): boolean {
    return this.picked?.setupPath === 'api_key';
  }

  /** An open MCP server — nothing to sign in to, so nothing to consent to. */
  get isOpen(): boolean {
    return this.picked?.setupPath === 'no_auth';
  }

  /** Entries with no consent step: the probe button is meaningless for both, and
   *  gating Connect behind it strands the admin on a button that never enables. */
  get skipsProbe(): boolean {
    return this.isDirect || this.isOpen || this.isApiKey;
  }

  get canCreate(): boolean {
    // Direct entries carry their endpoints and scopes in the catalog, so there is
    // nothing to discover and nothing to wait for.
    if (this.skipsProbe) {
      // A key service is not connectable without the key — that IS the credential,
      // so an empty field must not enable Add.
      if (this.isApiKey && !this.apiKey.trim()) {
        return false;
      }
      return !!this.label.trim() && !!this.key.trim();
    }
    return (
      !!this.discovery &&
      !this.discovery.problem &&
      !!this.label.trim() &&
      !!this.key.trim() &&
      (!this.needsManualClient || !!this.clientId.trim())
    );
  }

  create(): void {
    if (!this.canCreate || this.isCreating) {
      return;
    }
    this.isCreating = true;
    this.connectorsService
      .create({
        // A catalog pick carries its own identity server-side; the explicit
        // fields are what the paste-a-URL path needs.
        catalogKey: this.picked?.key,
        key: this.key.trim().toLowerCase() || undefined,
        label: this.label.trim() || undefined,
        mcpUrl: this.mcpUrl.trim() || undefined,
        clientId: this.clientId.trim() || undefined,
        clientSecret: this.clientSecret.trim() || undefined,
        apiKey: this.apiKey.trim() || undefined,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.isCreating = false;
          this.showAdd = false;
          this.resetAddForm();
          this.reload();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.isCreating = false;
          // The server distinguishes "you must register an app yourself" from a
          // plain failure, because only the first is something the admin can act on.
          if (err?.error?.needsManualClient) {
            this.needsManualClient = true;
            this.discoverError =
              err.error.err ||
              this.translateService.instant(
                'This provider needs an OAuth app you create yourself',
              );
            return;
          }
          this.toastError(err?.error?.err || 'Could not add that connector');
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Send the admin to the provider's consent screen.
   *
   * Opened in a new tab rather than redirecting, so they keep this page — and so the
   * warning below is the last thing they read first. The consent screen authorizes
   * **whoever is currently signed in to that provider in this browser**, which is
   * exactly how somebody's personal account quietly becomes the account the whole
   * organization's agent acts as.
   */
  connect(c: Connector): void {
    this.connectorsService
      .authorize(c.key, c.instanceId)
      .pipe(untilDestroyed(this))
      .subscribe({
      next: (res) => {
        if (res.authorizationUrl) {
          window.open(res.authorizationUrl, '_blank', 'noopener');
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.toastError(err?.error?.err || 'Could not start authorization');
        this.cdr.markForCheck();
      },
    });
  }

  remove(c: Connector): void {
    this.connectorsService
      .remove(c.key, c.instanceId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          // Drop it from the list HERE rather than waiting for reload() to tell
          // us. Observed live: the DELETE returned 200 and the row really was
          // gone from the database, but the list still showed it — so the click
          // read as "nothing happened" and invited a second click on a connector
          // that had already been removed. Whatever the reload lost (a stale
          // read, a missed render), the outcome is already known at this point:
          // the server said the delete succeeded.
          this.connectors = this.connectors.filter((x) => x.key !== c.key);
          this.toastSuccess(`${c.label} removed`);
          // Still reconcile with the server, so anything else that changed shows
          // up and a wrong optimistic guess cannot persist.
          this.reload();
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastError('Could not remove that connector');
          this.cdr.markForCheck();
        },
      });
  }

  statusLabel(c: Connector): string {
    if (c.status === 'connected') {
      return this.translateService.instant('Connected');
    }
    if (c.status === 'needs_reauth') {
      return this.translateService.instant('Needs reconnecting');
    }
    return this.translateService.instant('Not connected');
  }

  private toastSuccess(message: string): void {
    this.notificationToaster.showNotification(
      new V2NotificationDataBuilder('connectors.success')
        .type(NotificationType.SUCCESS)
        .style(ToasterPopupStyle.SUCCESS)
        .title(this.translateService.instant(message))
        .timeOut(4)
        .dismissable(true)
        .build(),
    );
  }

  private toastError(message: string): void {
    this.notificationToaster.showNotification(
      new V2NotificationDataBuilder('connectors.error')
        .type(NotificationType.ERROR)
        .style(ToasterPopupStyle.ERROR)
        .title(this.translateService.instant(message))
        .timeOut(6)
        .dismissable(true)
        .build(),
    );
  }
}
